import { z } from "zod";

import fetch, { type ResponseConfig } from "@/shared/lib/api";
import type { GoodLuckCoreDTOsTournamentCreateRequest } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/TournamentCreateRequest";
import { deleteTournamentsId } from "@/shared/api/generated-kubb/hooks/useDeleteTournamentsId";
import { deleteTournamentsIdDiscordChannels } from "@/shared/api/generated-kubb/hooks/useDeleteTournamentsIdDiscordChannels";
import { getTournaments } from "@/shared/api/generated-kubb/hooks/useGetTournaments";
import { getTournamentsDraftCaptainBudgetDefault } from "@/shared/api/generated-kubb/hooks/useGetTournamentsDraftCaptainBudgetDefault";
import { getTournamentsDraftRankCostsDefaults } from "@/shared/api/generated-kubb/hooks/useGetTournamentsDraftRankCostsDefaults";
import { getTournamentsId } from "@/shared/api/generated-kubb/hooks/useGetTournamentsId";
import { getTournamentsIdParticipants } from "@/shared/api/generated-kubb/hooks/useGetTournamentsIdParticipants";
import { getTournamentsPaged } from "@/shared/api/generated-kubb/hooks/useGetTournamentsPaged";
import { getUserIdTournamentRegistrations } from "@/shared/api/generated-kubb/hooks/useGetUserIdTournamentRegistrations";
import { postTournaments } from "@/shared/api/generated-kubb/hooks/usePostTournaments";
import { postTournamentsIdCancel } from "@/shared/api/generated-kubb/hooks/usePostTournamentsIdCancel";
import { postTournamentsIdCloseRegistration } from "@/shared/api/generated-kubb/hooks/usePostTournamentsIdCloseRegistration";
import { postTournamentsIdPublish } from "@/shared/api/generated-kubb/hooks/usePostTournamentsIdPublish";
import { postTournamentsRegisterSolo } from "@/shared/api/generated-kubb/hooks/usePostTournamentsRegisterSolo";
import { postTournamentsRegisterTeam } from "@/shared/api/generated-kubb/hooks/usePostTournamentsRegisterTeam";
import { putTournamentsId } from "@/shared/api/generated-kubb/hooks/usePutTournamentsId";
import { putTournamentsIdAnnounce } from "@/shared/api/generated-kubb/hooks/usePutTournamentsIdAnnounce";
import type { GetTournamentsPagedQueryParams } from "@/shared/api/generated-kubb/types/GetTournamentsPaged";
import type { PostTournamentsMutationRequest } from "@/shared/api/generated-kubb/types/PostTournaments";
import type { PostTournamentsRegisterSoloMutationRequest } from "@/shared/api/generated-kubb/types/PostTournamentsRegisterSolo";
import type { PostTournamentsRegisterTeamMutationRequest } from "@/shared/api/generated-kubb/types/PostTournamentsRegisterTeam";
import type { PutTournamentsIdMutationRequest } from "@/shared/api/generated-kubb/types/PutTournamentsId";

import {
  CreateTournamentResponseSchema,
  UpdateTournamentResponseSchema,
  TournamentRegisterRequestSchema,
  TournamentRegisterResponseSchema,
  GetTournamentParticipantsResponseSchema,
  GetUserRegistrationsResponseSchema,
} from "./response-types.schemas";
import {
  CreateTournamentRequestSchema,
  UpdateTournamentRequestSchema,
  TournamentRegistrationSchema,
} from "./tournament.schemas";
import { goodLuckCoreDTOsTournamentParticipantDtoSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/tournamentParticipantDtoSchema";
import { parseRequestLoose } from "./parse-request-loose";
import type {
  TournamentTimelineResponse,
  UpdateTournamentTimelineRequest,
} from "./tournament-timeline-types";

export type Tournament = {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  description?: string | null;
  bannerUrl?: string | null;
  rules?: string | null;
  region?: string | null;
  status?: string | null;
  type?: string | null;
  format?: string | null;
  bracketType?: string | null;
  game?: string | null;
  prizePool?: number | null;
  prizeDescription?: string | null;
  prizeDistribution?: Array<{
    placeFrom: number;
    placeTo: number;
    prizeType?: string;
    sharePercent?: number | null;
    quantity?: number | null;
    description?: string | null;
  }> | null;
  currency?: string | null;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  maxPlayers?: number | null;
  currentTeams?: number | null;
  maxTeams?: number | null;
  image?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  createdAt?: string | null;
  registeredPlayersCount?: number | null;
  organizer?: {
    id?: string | null;
    name?: string | null;
    avatar?: string | null;
  } | null;
  organization?: {
    id?: string | null;
    name?: string | null;
    avatar?: string | null;
  } | null;
  createdById?: string | null;
  createdByName?: string | null;
  createdBy?: string | null;
  teams?: Array<{ id?: string | null; name?: string | null }> | null;
};

const TournamentApiSchema: z.ZodTypeAny = z.any();

export interface TournamentTeam {
  id: number;
  name: string;
  logo: string;
  seed: number;
  players: string[];
}

export interface TournamentSchedule {
  date: string;
  time: string;
  match: string;
  teams: string[];
}

/**
 * KAN-342: тип тела запроса берётся из СГЕНЕРИРОВАННОГО по OpenAPI DTO, а не из
 * рукописной zod-схемы. Схема писалась руками параллельно бэкенду и неизбежно
 * отставала (не было statsMode, autoStartMatches, officialStreamChannel,
 * showPlayerStreamers/ShowOfficialStream, useDynamicSchedule, roundBufferMinutes,
 * pausesAllowedPerMatch/pauseDurationMinutes — и наоборот, содержала несуществующие
 * на бэке game/currency/location). Из-за расхождения оба вызывающих кода писали
 * `payload as unknown as CreateTournamentRequest` — двойной каст полностью глушит
 * проверку, и поля терялись молча.
 *
 * Runtime-валидация по-прежнему идёт через zod (parseRequestLoose ниже) — она
 * НЕ режет лишние ключи, так что рассинхрон схемы больше не теряет данные, а
 * компилятор теперь сверяет payload с настоящим контрактом бэкенда.
 */
export type CreateTournamentRequest = GoodLuckCoreDTOsTournamentCreateRequest;

/** PUT /tournaments/{id} принимает тот же DTO, что и POST (см. TournamentsController). */
export type UpdateTournamentRequest = GoodLuckCoreDTOsTournamentCreateRequest;

export interface TournamentTemplateDto {
  id: string;
  name: string;
  organizationId: string;
  createdById: string;
  createdAt: string;
}

export interface TournamentTemplateWithSettingsDto extends TournamentTemplateDto {
  settings: CreateTournamentRequest;
}

export interface SaveAsTemplateRequest {
  name: string;
  tournamentId: string;
}

export interface CreateTournamentFromTemplateRequest {
  templateId: string;
  name: string;
  startDate: string;
  endDate: string;
}

export type TournamentRegistration = z.infer<
  typeof TournamentRegistrationSchema
>;

const TournamentPagedResponseSchema: z.ZodTypeAny = z.object({
  items: z.array(TournamentApiSchema),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  hasMore: z.boolean(),
});

export type TournamentPagedResponse = {
  items: Tournament[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
};

// API functions
export const tournamentApi = {
  getTournaments: async (params?: {
    status?: string;
    game?: string;
    page?: number;
    limit?: number;
  }): Promise<Tournament[]> => {
    const hasParams =
      !!params &&
      (params.status !== undefined ||
        params.game !== undefined ||
        params.page !== undefined ||
        params.limit !== undefined);

    if (hasParams) {
      const response = (await getTournamentsPaged(
        params as GetTournamentsPagedQueryParams,
      )) as unknown as ResponseConfig<unknown>;
      const parsed = TournamentPagedResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        console.error("❌ Tournament paged schema validation failed:", parsed.error);
        throw parsed.error;
      }
      return (parsed.data as { items: Tournament[] }).items;
    }

    const response = (await getTournaments()) as unknown as ResponseConfig<unknown>;

    // Backend returns array directly, not wrapped in object
    const parsed = z.array(TournamentApiSchema).safeParse(response.data);
    if (!parsed.success) {
      console.error("❌ Tournament schema validation failed:", parsed.error);
      throw parsed.error;
    }
    return parsed.data as Tournament[];
  },

  getTournamentsPaged: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sortBy?: "startDate" | "prizePool" | "participants" | string;
    sortDir?: "asc" | "desc" | string;
  }): Promise<TournamentPagedResponse> => {
    const response = (await getTournamentsPaged(
      params as GetTournamentsPagedQueryParams,
    )) as unknown as ResponseConfig<unknown>;
    const parsed = TournamentPagedResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error(
        "❌ Tournament paged schema validation failed:",
        parsed.error,
      );
      throw parsed.error;
    }
    return parsed.data as TournamentPagedResponse;
  },

  getTournamentById: async (tournamentId: string): Promise<Tournament> => {
    const response = (await getTournamentsId(
      tournamentId,
    )) as unknown as ResponseConfig<unknown>;

    // Backend returns tournament directly, not wrapped in object
    const parsed = TournamentApiSchema.safeParse(response.data);
    if (!parsed.success) {
      console.error(
        "❌ Tournament by ID schema validation failed:",
        parsed.error,
      );
      throw parsed.error;
    }
    return parsed.data as Tournament;
  },

  getDraftRankCostsDefaults: async (): Promise<Record<string, number>> => {
    const response =
      (await getTournamentsDraftRankCostsDefaults()) as unknown as ResponseConfig<unknown>;
    return response.data as Record<string, number>;
  },

  getDraftCaptainBudgetDefault: async (): Promise<number> => {
    const response =
      (await getTournamentsDraftCaptainBudgetDefault()) as unknown as ResponseConfig<unknown>;
    return response.data as number;
  },

  createTournament: async (
    data: CreateTournamentRequest,
  ): Promise<{ id: string; success: boolean }> => {
    // Валидация входящих данных
    const validatedData = parseRequestLoose(CreateTournamentRequestSchema, data);

    const response = (await postTournaments(
      validatedData as PostTournamentsMutationRequest,
    )) as unknown as ResponseConfig<unknown>;

    // Валидация исходящих данных
    const parsed = CreateTournamentResponseSchema.safeParse(response.data);
    if (!parsed.success) throw parsed.error;
    return parsed.data;
  },

  updateTournament: async (
    tournamentId: string,
    data: UpdateTournamentRequest,
  ): Promise<Tournament> => {
    const validatedData = parseRequestLoose(UpdateTournamentRequestSchema, data);

    const response = (await putTournamentsId(
      tournamentId,
      validatedData as PutTournamentsIdMutationRequest,
    )) as unknown as ResponseConfig<unknown>;

    const parsed = UpdateTournamentResponseSchema.safeParse(response.data);
    if (!parsed.success) throw parsed.error;
    return parsed.data.tournament as Tournament;
  },

  updateTournamentBanner: async (
    tournamentId: string,
    image: string | undefined,
  ): Promise<Tournament> => {
    const response = (await fetch({
      method: "PUT",
      url: `/tournaments/${tournamentId}/banner`,
      data: { image: image?.trim() || null },
    })) as unknown as ResponseConfig<{ tournament?: unknown }>;
    const parsed = UpdateTournamentResponseSchema.safeParse(response.data);
    if (!parsed.success) throw parsed.error;
    return parsed.data.tournament as Tournament;
  },

  saveAsTemplate: async (
    tournamentId: string,
    name: string,
  ): Promise<TournamentTemplateDto> => {
    const response = (await fetch({
      method: "POST",
      url: "/tournaments/save-as-template",
      data: { tournamentId, name: name.trim() },
    })) as unknown as ResponseConfig<TournamentTemplateDto>;
    return response.data;
  },

  getTemplates: async (
    organizationId: string,
  ): Promise<TournamentTemplateDto[]> => {
    const response = (await fetch({
      method: "GET",
      url: "/tournaments/templates",
      params: { organizationId },
    })) as unknown as ResponseConfig<TournamentTemplateDto[]>;
    return response.data ?? [];
  },

  getTemplateWithSettings: async (
    id: string,
  ): Promise<TournamentTemplateWithSettingsDto> => {
    const response = (await fetch({
      method: "GET",
      url: `/tournaments/templates/${id}`,
    })) as unknown as ResponseConfig<TournamentTemplateWithSettingsDto>;
    return response.data;
  },

  createFromTemplate: async (
    data: CreateTournamentFromTemplateRequest,
  ): Promise<{ id: string; success: boolean }> => {
    const response = (await fetch({
      method: "POST",
      url: "/tournaments/create-from-template",
      data,
    })) as unknown as ResponseConfig<{ id: string; success: boolean }>;
    return response.data;
  },

  deleteTournament: async (tournamentId: string): Promise<void> => {
    await deleteTournamentsId(tournamentId);
  },

  cancelTournament: async (tournamentId: string): Promise<{ success: boolean }> => {
    const response = (await postTournamentsIdCancel(
      tournamentId,
    )) as unknown as ResponseConfig<unknown>;
    return response.data as { success: boolean };
  },

  deleteDiscordChannels: async (tournamentId: string): Promise<void> => {
    await deleteTournamentsIdDiscordChannels(tournamentId);
  },

  // Register for tournament (deprecated - use specific methods below)
  registerForTournament: async (
    tournamentId: string,
    teamId?: string,
  ): Promise<TournamentRegistration> => {
    // Валидация входящих данных
    const validatedData = parseRequestLoose(TournamentRegisterRequestSchema, { teamId });

    const response = (await fetch({
      method: "POST",
      url: `/tournaments/${tournamentId}/register`,
      data: validatedData,
    })) as unknown as ResponseConfig<unknown>;

    // Валидация исходящих данных
    const parsed = TournamentRegisterResponseSchema.safeParse(response.data);
    if (!parsed.success) throw parsed.error;
    return parsed.data.registration;
  },

  // Register solo player for tournament (Draft mode)
  registerSoloPlayer: async (data: {
    tournamentId: string;
    riotAccountId: string;
    /** Не передан = «любая роль»: игрок готов на любую линию. */
    mainRole?: string;
    secondaryRole?: string;
    desiredRole: "Participant" | "Captain";
    /** Заметка игрока о себе для капитанов (до 200 символов). */
    playerNote?: string;
    customFields?: Record<string, string> | null;
  }): Promise<{ success: boolean }> => {
    const response = (await postTournamentsRegisterSolo(
      data as PostTournamentsRegisterSoloMutationRequest,
    )) as unknown as ResponseConfig<unknown>;
    return response.data as { success: boolean };
  },

  // Register team for tournament (Classic mode)
  registerTeam: async (data: {
    tournamentId: string;
    teamId?: string;
    riotAccountId: string;
    customFields?: Record<string, string> | null;
  }): Promise<{ success: boolean }> => {
    const response = (await postTournamentsRegisterTeam(
      data as PostTournamentsRegisterTeamMutationRequest,
    )) as unknown as ResponseConfig<unknown>;
    return response.data as { success: boolean };
  },

  // Unregister from tournament
  unregisterFromTournament: async (tournamentId: string): Promise<void> => {
    await fetch({ method: "DELETE", url: `/tournaments/${tournamentId}/register` });
  },

  getTournamentParticipants: async (
    tournamentId: string,
  ): Promise<z.infer<typeof goodLuckCoreDTOsTournamentParticipantDtoSchema>[]> => {
    const response = (await getTournamentsIdParticipants(
      tournamentId,
    )) as unknown as ResponseConfig<unknown>;

    // Валидация исходящих данных
    const parsed = GetTournamentParticipantsResponseSchema.safeParse(
      response.data,
    );
    if (!parsed.success) throw parsed.error;
    return parsed.data;
  },

  getUserRegistrations: async (
    userId: string,
  ): Promise<TournamentRegistration[]> => {
    const response = (await getUserIdTournamentRegistrations(
      userId,
    )) as unknown as ResponseConfig<unknown>;

    // Валидация исходящих данных
    const parsed = GetUserRegistrationsResponseSchema.safeParse(response.data);
    if (!parsed.success) throw parsed.error;
    return parsed.data.registrations;
  },

  // Announce tournament (change status from Upcoming to RegistrationOpen)
  announceTournament: async (tournamentId: string): Promise<void> => {
    await putTournamentsIdAnnounce(tournamentId);
  },

  // Publish tournament (change status from Draft to Upcoming)
  publishTournament: async (tournamentId: string): Promise<void> => {
    await postTournamentsIdPublish(tournamentId);
  },

  closeRegistration: async (tournamentId: string): Promise<void> => {
    await postTournamentsIdCloseRegistration(tournamentId);
  },


  getRulebook: async (
    tournamentId: string,
  ): Promise<TournamentRulebookResponse | null> => {
    const response = (await fetch({
      method: "GET",
      url: `/tournaments/${tournamentId}/rulebook`,
    })) as unknown as ResponseConfig<TournamentRulebookResponse | null>;
    return response.data ?? null;
  },

  updateRulebook: async (
    tournamentId: string,
    data: TournamentRulebookUpdateRequest,
  ): Promise<TournamentRulebookResponse> => {
    const response = (await fetch({
      method: "PUT",
      url: `/tournaments/${tournamentId}/rulebook`,
      data,
    })) as unknown as ResponseConfig<TournamentRulebookResponse>;
    return response.data as TournamentRulebookResponse;
  },

  getTimeline: async (
    tournamentId: string,
  ): Promise<TournamentTimelineResponse | null> => {
    const response = (await fetch({
      method: "GET",
      url: `/tournaments/${tournamentId}/timeline`,
    })) as unknown as ResponseConfig<TournamentTimelineResponse | null>;
    return response.data ?? null;
  },

  updateTimeline: async (
    tournamentId: string,
    data: UpdateTournamentTimelineRequest,
  ): Promise<TournamentTimelineResponse> => {
    const response = (await fetch({
      method: "PUT",
      url: `/tournaments/${tournamentId}/timeline`,
      data,
    })) as unknown as ResponseConfig<TournamentTimelineResponse>;
    return response.data;
  },

  advanceTimelineStage: async (
    tournamentId: string,
  ): Promise<TournamentTimelineResponse> => {
    const response = (await fetch({
      method: "POST",
      url: `/tournaments/${tournamentId}/timeline/advance`,
    })) as unknown as ResponseConfig<TournamentTimelineResponse>;
    return response.data;
  },
};

export type RulebookWinConditionType = "Kill" | "CreepScore100" | "TowerDestroyed";

export interface TournamentRulebookResponse {
  id: string;
  tournamentId: string;
  timeoutMinutes?: number | null;
  pausePerTeamMinutes?: number | null;
  pausePerGameMinutes?: number | null;
  pauseBetweenGamesMinutes?: number | null;
  pauseRules?: string | null;
  rehostRules?: string | null;
  bannedChampionIds: number[];
  bannedRuneIds: number[];
  bannedItemIds?: number[];
  bannedSummonerSpellIds?: number[];
  defaultWinCondition?: RulebookWinConditionType | null;
  attachmentUrl?: string | null;
  attachmentFileName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TournamentRulebookUpdateRequest {
  timeoutMinutes?: number | null;
  pausePerTeamMinutes?: number | null;
  pausePerGameMinutes?: number | null;
  pauseBetweenGamesMinutes?: number | null;
  pauseRules?: string | null;
  rehostRules?: string | null;
  bannedChampionIds?: number[] | null;
  bannedRuneIds?: number[] | null;
  bannedItemIds?: number[] | null;
  bannedSummonerSpellIds?: number[] | null;
  defaultWinCondition?: RulebookWinConditionType | null;
  attachmentUrl?: string | null;
  attachmentFileName?: string | null;
}

export type {
  TournamentTimelineResponse,
  TournamentTimelineStageDto,
  TournamentTimelineStageItemDto,
  UpdateTournamentTimelineRequest,
} from "./tournament-timeline-types";
